import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation, useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import IconButton from '@material-ui/core/IconButton';
import DehazeIcon from '@material-ui/icons/Dehaze';
import { userHasSomePermissions } from 'utils/auth';
import { ReactComponent as LogoSvg } from 'assets/ds-logo.svg';
import LinkedMenuItem from './LinkedMenuItem';
import CollapsibleMenuItem from './CollapsibleMenuItem';
import Profile from './Profile';

const styles = (theme) => ({
  drawer: {
    width: theme.dimension.sideBarWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    color: theme.palette.xpl.foreground,
  },
  drawerOpen: {
    width: theme.dimension.sideBarWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.xpl.navigation,
    borderRight: 0,
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 0,
    backgroundColor: theme.palette.xpl.navigation,
    borderRight: 0,
  },
  closeIcon: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: '0 0 16px 16px',
    // ...theme.mixins.toolbar,
    minHeight: 78,
    flexDirection: 'column',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  imageContainer: {
    paddingRight: '24px',
    cursor: 'pointer',
  },
  itemText: {
    color: theme.palette.xpl.foreground,
  },
  icon: {
    color: theme.palette.common.white,
  },
  list: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
  },
  listContent: {
    flex: 1,
  },
  profile: {
    textAlign: 'center',
    padding: '18px 24px',
    backgroundColor: theme.palette.xpl.dm.background2,
    color: theme.palette.xpl.foreground,
  },
  paperAnchorDockedLeft: {
    borderRight: 0,
  },
});

const filterByFeatureFlags = (menuItem, featureFlags) => {
  return !menuItem.featureFlag || featureFlags[menuItem.featureFlag];
};

const filterByPermissions = (menuItem, userPermissions) => {
  return (
    !menuItem.roles ||
    userHasSomePermissions(userPermissions, ...menuItem.roles.split(','))
  );
};

const SideMenu = (props) => {
  const {
    classes,
    userPermissions,
    isSideBarOpen,
    handleSideBarClose,
    sideMenuEntries,
    profileName,
    profileMenuEntries,
    featureFlags,
  } = props;

  const { pathname } = useLocation();
  const history = useHistory();

  const filterMenuItems = () => {
    const isMenuItemSelected = (menuPath) =>
      menuPath && pathname.toLowerCase().startsWith(menuPath.toLowerCase());

    return sideMenuEntries
      .filter(
        (menuItem) =>
          filterByFeatureFlags(menuItem, featureFlags) &&
          filterByPermissions(
            menuItem,
            userPermissions,
            ...menuItem.roles.split(',')
          )
      )
      .map((menuItem) => {
        return menuItem.subRoutes
          ? {
              ...menuItem,
              selected: menuItem.subRoutes.some((subRoute) =>
                isMenuItemSelected(subRoute.path)
              ),
              subRoutes: menuItem.subRoutes
                .filter(
                  (subRouteItem) =>
                    filterByFeatureFlags(subRouteItem, featureFlags) &&
                    filterByPermissions(
                      subRouteItem,
                      userPermissions,
                      ...subRouteItem.roles.split(',')
                    ) &&
                    !subRouteItem.hidden
                )
                .map((subRoute) => ({
                  ...subRoute,
                  selected: isMenuItemSelected(subRoute.path),
                })),
            }
          : {
              ...menuItem,
              selected: isMenuItemSelected(menuItem.path),
            };
      });
  };

  const filteredMenuItems = filterMenuItems();

  const [openCollapsibleItem, setOpenCollapsibleItem] = useState(() => {
    const initalSelectedMenuItem = filteredMenuItems.find(
      (item) => item.selected
    );
    return initalSelectedMenuItem ? initalSelectedMenuItem.name : '';
  });

  const handleCollapsibleMenuItemClick = useCallback(
    (name) =>
      setOpenCollapsibleItem((prevOpenItem) =>
        name === prevOpenItem ? null : name
      ),
    [setOpenCollapsibleItem]
  );

  const handleLogoClick = () => {
    history.push('/Customers');
  };

  return (
    <Drawer
      variant="persistent"
      className={classNames(classes.drawer, {
        [classes.drawerOpen]: isSideBarOpen,
        [classes.drawerClose]: !isSideBarOpen,
      })}
      classes={{
        paper: classNames({
          [classes.drawerOpen]: isSideBarOpen,
          [classes.drawerClose]: !isSideBarOpen,
        }),
      }}
      open={isSideBarOpen}>
      <div className={classes.listContent}>
        <div className={classes.toolbar}>
          <IconButton
            onClick={handleSideBarClose}
            className={classes.closeIcon}>
            <DehazeIcon className={classes.icon} />
          </IconButton>
          <div
            role="none"
            className={classes.imageContainer}
            onClick={handleLogoClick}>
            <LogoSvg />
          </div>
        </div>
        <List>
          {filteredMenuItems.map((menuItem) => {
            const {
              subRoutes,
              selected,
              icon,
              name,
              isExternal,
              path,
            } = menuItem;

            return menuItem.subRoutes && menuItem.subRoutes.length > 0 ? (
              <CollapsibleMenuItem
                selected={selected}
                Icon={icon}
                name={name}
                subMenuItems={subRoutes}
                key={name}
                expand={name === openCollapsibleItem}
                onCollapsibleMenuClick={handleCollapsibleMenuItemClick}
                classes={{
                  itemText: classes.itemText,
                  subMenuItemText: classes.itemText,
                }}
              />
            ) : !menuItem.subRoutes ? (
              <LinkedMenuItem
                key={name}
                name={name}
                path={path}
                Icon={icon}
                selected={selected}
                isExternal={isExternal}
                classes={{ itemText: classes.itemText }}
              />
            ) : (
              <div />
            );
          })}
        </List>
      </div>

      {isSideBarOpen && (
        <div className={classes.profile}>
          <Profile
            profileName={profileName}
            profileMenuEntries={profileMenuEntries}
          />
        </div>
      )}
    </Drawer>
  );
};

SideMenu.propTypes = {
  classes: PropTypes.object.isRequired,
  isSideBarOpen: PropTypes.bool.isRequired,
  handleSideBarClose: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  sideMenuEntries: PropTypes.array.isRequired,
  userPermissions: PropTypes.array,
  profileName: PropTypes.string.isRequired,
  profileMenuEntries: PropTypes.array.isRequired,
  featureFlags: PropTypes.object,
};

SideMenu.defaultProps = {
  userPermissions: [],
  featureFlags: {},
};

export default withStyles(styles, { withTheme: true })(SideMenu);
